/**
 * Every app and its support content lives here. One file, one place to edit.
 *
 * TO ADD AN APP: copy this shape into the APPS array below. Only `slug`,
 * `name` and `status` are required; everything else degrades gracefully.
 *
 *   {
 *     slug:     'url-name',        // becomes /support/url-name (lookup is case-insensitive)
 *     name:     'Display Name',
 *     tagline:  'One line for the app card.',
 *     summary:  'A short paragraph shown on the support page.',
 *     platform: 'iOS',             // or 'macOS'
 *     status:   'live',            // 'live' | 'soon'
 *     store: {                     // omit or null if it is not on the App Store yet
 *       url:     'https://apps.apple.com/us/app/.../id0000000000',
 *       price:   'Free',
 *       version: '1.0',
 *     },
 *     image:  'icon.png',          // file in public/images, or null for a lettered tile
 *     monogram: 'RI',              // optional: overrides the letters on that tile
 *     accent: ['#6366f1', '#22d3ee'],   // gradient used by that lettered tile
 *     listed: true,                // false keeps the route but hides the card
 *     policy: { ... },             // see the policy helpers below
 *   }
 */

export const CONTACT_EMAIL = 'aftershare@proton.me';
export const OWNER = 'Aurescope Interactive';

/* -------------------------------------------------------------------------
   Reusable policy fragments
   ------------------------------------------------------------------------- */

const POLICY_CHANGES = {
  title: 'Policy Changes',
  body:
    'We may update this policy from time to time. Significant changes will be ' +
    'posted in the app or reflected by updating the "Last updated" date above. ' +
    'Continuing to use the app means you accept the revised policy.',
};

/** Policy shared by the apps that genuinely collect nothing. */
const noCollectionPolicy = ({ name, updated, extra }) => ({
  updated,
  intro: `Thank you for using ${name}. This policy describes how we collect, use, and protect your personal information when you use our services.`,
  glance: [
    { tone: 'yes', title: 'No personal data collected', note: 'The app does not gather information that identifies you.' },
    { tone: 'yes', title: 'No tracking or ads', note: 'No advertising identifiers and no third-party analytics.' },
    { tone: 'yes', title: 'Nothing is sold or shared', note: 'There is no data to sell, rent, or hand to advertisers.' },
  ],
  sections: [
    {
      title: 'Information We Collect',
      lead: 'No information is collected at this time.',
      body: extra,
    },
    POLICY_CHANGES,
  ],
});

/* -------------------------------------------------------------------------
   The "visited counties" policy
   ------------------------------------------------------------------------- */

const countyPolicy = name => ({
  updated: 'February 8, 2025',
  intro: `Thank you for using ${name}. This policy describes how we collect, use, and protect your personal information when you use our services.`,
  glance: [
    { tone: 'care', title: 'Location is used', note: 'Only to detect when you enter a new county. It is never shared with third parties.' },
    { tone: 'info', title: 'Optional iCloud sync', note: 'Visited counties can sync across your devices through Apple CloudKit.' },
    { tone: 'yes', title: 'Never sold to advertisers', note: 'Your location data is not sold or shared with third-party advertisers.' },
    { tone: 'yes', title: 'You stay in control', note: 'Revoke location access, disable iCloud, or clear your visit data at any time.' },
  ],
  sections: [
    {
      title: 'Information We Collect',
      subs: [
        {
          title: 'Location Data',
          body:
            'The app uses your device’s location to identify when you have entered a new county. ' +
            'We use this data solely to update your visited counties list. We do not continuously ' +
            'track your location in the background unless you enable "Always" location permissions. ' +
            'Even then, it is used only to update visited counties, and never shared with third parties.',
        },
        {
          title: 'Device & Usage Data',
          body:
            'We may collect non-personal usage data, such as crash reports or anonymized analytics, ' +
            'to improve app performance and user experience. This data does not contain information ' +
            'that personally identifies you.',
        },
        {
          title: 'Saved County Data',
          body:
            'When you mark a county as visited, we store that information, including any random ' +
            'color assigned to that county, in our local database (Core Data) and, if enabled, in ' +
            'iCloud via CloudKit. The data may include a stable identifier for the county (such as a ' +
            'geoID) and a timestamp of when you visited it.',
        },
      ],
    },
    {
      title: 'How We Use Your Information',
      subs: [
        {
          title: 'Improve & Personalize the Experience',
          body:
            'We use your location data to highlight visited counties on the map. We store your county ' +
            'visit data so the app can display your visited counties list across sessions and devices ' +
            '(if using iCloud).',
        },
        {
          title: 'Sync with iCloud (Optional)',
          body:
            'If iCloud is enabled and you are signed in, your visited county data will be synced via ' +
            'CloudKit so you can access it across multiple devices.',
        },
        {
          title: 'Analytics & Diagnostics',
          body:
            'We may analyze aggregated usage data, such as session duration or crash reports, to ' +
            'troubleshoot and enhance app features. This data does not identify you personally.',
        },
      ],
    },
    {
      title: 'Data Sharing & Disclosure',
      subs: [
        {
          title: 'Third Parties',
          body:
            'We do not sell or share your personal location data with third-party advertisers. We only ' +
            'share necessary diagnostic data with service providers (like Apple’s CloudKit) for app ' +
            'functionality and improvements.',
        },
        {
          title: 'Legal Requirements',
          body:
            'We may disclose your data if required by law, or in the good-faith belief that such action ' +
            'is necessary to comply with legal obligations or protect our rights.',
        },
      ],
    },
    {
      title: 'Data Retention & Security',
      subs: [
        {
          title: 'Retention',
          body:
            'We retain your visited county data (including location timestamps) in Core Data and/or ' +
            'CloudKit as long as the app is installed. Removing the app or clearing app data can delete ' +
            'your visits locally. For iCloud data, removing the app does not automatically delete iCloud ' +
            'records unless you explicitly remove them via the app’s settings or iCloud settings.',
        },
        {
          title: 'Security',
          body:
            'We strive to use commercially acceptable means to protect your personal data, including ' +
            'encrypted communication with Apple’s CloudKit servers. No method of transmission or storage ' +
            'is 100% secure; while we take precautions, we cannot guarantee absolute security.',
        },
      ],
      callout: {
        tone: 'warn',
        title: 'Deleting the app does not clear iCloud',
        body:
          'If iCloud sync is on, your records stay in iCloud until you remove them from the app’s ' +
          'settings or from your iCloud settings.',
      },
    },
    {
      title: 'Your Choices',
      subs: [
        {
          title: 'Location Permissions',
          body:
            'You can revoke location access at any time in your device’s Settings. Note that without ' +
            'location access, the app cannot automatically mark counties as visited.',
        },
        {
          title: 'iCloud Sync',
          body:
            'You can opt out of iCloud syncing by disabling iCloud for our app in your device’s Settings, ' +
            'but you will lose the cross-device synchronization feature.',
        },
        {
          title: 'Deletion',
          body:
            'To remove all your saved county data, use the app’s "Clear Visited Data" feature (if provided) ' +
            'or delete the app. If iCloud is enabled, you may also need to remove iCloud records from your ' +
            'iCloud settings.',
        },
      ],
    },
    POLICY_CHANGES,
  ],
});

/* -------------------------------------------------------------------------
   App registry
   ------------------------------------------------------------------------- */

export const APPS = [
  {
    slug: 'my-advantage',
    name: 'My Tennis Advantage',
    tagline: 'Tennis logging, string jobs and insights.',
    summary:
      'Tennis tracking for players who care about the details. Log practices and matches in ' +
      'seconds, then keep a record of every string job with tension, gauge, layout and ' +
      'playability ratings. Insights pulls it all together into head to head records, string ' +
      'comparisons, weekly hours and surface breakdowns.',
    platform: 'iOS',
    status: 'live',
    store: {
      url: 'https://apps.apple.com/us/app/my-tennis-advantage/id6757944011',
      price: '$12.99',
      version: '1.3',
    },
    image: 'my-advantage.png',
    accent: ['#6366f1', '#22d3ee'],
    intro:
      'We are here to help with any questions, issues, or suggestions about your training, matches, and progress tracking.',
    policy: {
      updated: 'February 8, 2025',
      intro:
        'Thank you for using My Advantage. This policy explains how we collect, use, and protect your information when you use the app.',
      glance: [
        { tone: 'yes', title: 'Your data stays on your device', note: 'Sessions, notes and expenses are stored locally.' },
        { tone: 'info', title: 'Optional iCloud sync', note: 'Turn it on to reach your log across devices. It is controlled in your device settings.' },
        { tone: 'yes', title: 'Never sold to advertisers', note: 'We do not sell, rent, or share your personal data.' },
        { tone: 'yes', title: 'Edit or delete anytime', note: 'Every session, note and expense can be changed or removed in-app.' },
      ],
      sections: [
        {
          title: 'Information We Collect',
          subs: [
            {
              title: 'Training & Match Data',
              body:
                'My Advantage lets you log practice sessions, matches, skill notes, results, duration, and ' +
                'optional comments. This data is stored locally on your device and is used solely to provide ' +
                'progress tracking, statistics, and history views.',
            },
            {
              title: 'Expense & Equipment Data',
              body:
                'If you choose to track expenses or equipment, the app stores purchase details, costs, and ' +
                'notes locally on your device to help you understand your investment in training.',
            },
            {
              title: 'Device & Usage Data',
              body:
                'We may collect non-personal diagnostic information such as crash reports or anonymized usage ' +
                'statistics to improve app stability and performance. This data does not personally identify you.',
            },
          ],
        },
        {
          title: 'How We Use Your Information',
          subs: [
            {
              title: 'Improve Your Training Experience',
              body:
                'Your logged sessions and notes are used to generate insights such as XP progression, session ' +
                'history, monthly summaries, and performance trends.',
            },
            {
              title: 'iCloud Sync (Optional)',
              body:
                'If enabled, your data may be synced securely using Apple iCloud so it can be accessed across ' +
                'your devices. iCloud sync is optional and can be disabled at any time in your device settings.',
            },
          ],
        },
        {
          title: 'Data Sharing & Disclosure',
          subs: [
            {
              title: 'Third Parties',
              body:
                'We do not sell, rent, or share your personal data with advertisers. Data is only shared with ' +
                'Apple services (such as iCloud) when necessary to provide optional syncing functionality.',
            },
            {
              title: 'Legal Requirements',
              body:
                'We may disclose information if required to do so by law or in response to valid legal requests.',
            },
          ],
        },
        {
          title: 'Data Retention & Security',
          subs: [
            {
              title: 'Retention',
              body:
                'Your training, match, and expense data is retained as long as the app remains installed. ' +
                'Deleting the app will remove locally stored data. If iCloud sync is enabled, data stored in ' +
                'iCloud may persist until removed through iCloud settings.',
            },
            {
              title: 'Security',
              body:
                'We take reasonable measures to protect your data, including secure storage and encrypted ' +
                'communication with Apple services. However, no system is completely secure.',
            },
          ],
        },
        {
          title: 'Your Choices',
          subs: [
            {
              title: 'Data Control',
              body: 'You can edit or delete your sessions, notes, and expenses at any time within the app.',
            },
            {
              title: 'iCloud Sync',
              body: 'You may enable or disable iCloud syncing at any time through your device’s settings.',
            },
          ],
        },
        POLICY_CHANGES,
      ],
    },
  },

  {
    slug: 'american-palette',
    name: 'American Palette',
    tagline: 'Color in every county you visit.',
    summary:
      'There are more than 3,143 counties in the United States and most people cross them ' +
      'without ever noticing. Leave American Palette running while you travel and each county ' +
      'fills in with its own color the moment you cross the line, numbered in the order you ' +
      'found it. No check-ins and no buttons to press. Manual mode lets you tap in counties ' +
      'the app missed, or backfill an old trip, and everything after it renumbers itself.',
    platform: 'iOS',
    status: 'live',
    store: {
      url: 'https://apps.apple.com/us/app/american-palette/id6741702484',
      price: '$19.99',
      version: '2.3.1',
    },
    image: 'icon1.png',
    accent: ['#d946ef', '#f97316'],
    policy: countyPolicy('American Palette'),
  },

  {
    slug: 'item-value',
    name: 'RAMPA',
    tagline: 'See what your Mac’s memory is really doing.',
    summary:
      'A memory monitor for macOS that answers a specific question: would more RAM actually ' +
      'improve your workflow? Instead of judging your Mac from a single snapshot, RAMPA watches ' +
      'active memory, compression, swap and pressure across your real workload, then reports ' +
      'session averages, peaks and trends from a dashboard or the menu bar.',
    platform: 'macOS',
    status: 'live',
    store: {
      url: 'https://apps.apple.com/us/app/rampa-ram-pressure-analyzer/id6742392178?mt=12',
      price: '$14.99',
      version: '2.0',
    },
    image: 'item-value.png',
    accent: ['#f59e0b', '#ef4444'],
    policy: noCollectionPolicy({
      name: 'RAMPA',
      updated: 'February 8, 2025',
    }),
  },

  {
    slug: 'Impulse-Market',
    name: 'Impulse Market',
    tagline: 'Shop all you want. Spend nothing.',
    summary:
      'A shopping simulator for the moment the urge to buy something hits. Browse, fill a cart ' +
      'and check out, except the balances, the credit and the deliveries are all pretend. ' +
      'Nothing is ever purchased, charged or shipped. Open fake credit cards, track fake ' +
      'deliveries, watch a car depreciate the second you buy it, and keep your real money ' +
      'where it belongs.',
    platform: 'iOS',
    status: 'live',
    store: {
      url: 'https://apps.apple.com/us/app/impulse-market/id6779297608',
      price: '$9.99',
      version: '1.2',
    },
    image: null,
    accent: ['#34d399', '#6366f1'],
    policy: noCollectionPolicy({
      name: 'Impulse Market',
      updated: 'February 8, 2025',
    }),
  },

  {
    slug: 'study-wizard',
    name: 'The Study Wizard',
    tagline: 'A study timer that levels you up.',
    summary:
      'An interactive study timer built to make the hours go down easier. You earn XP for ' +
      'every minute you study and level up as the sessions add up.',
    platform: 'iOS',
    status: 'live',
    store: {
      url: 'https://apps.apple.com/us/app/the-study-wizard/id6742042217',
      price: 'Free',
      version: '2.0',
    },
    image: 'icon.png',
    accent: ['#8b5cf6', '#22d3ee'],
    policy: noCollectionPolicy({
      name: 'The Study Wizard',
      updated: 'February 8, 2025',
      extra:
        'No data is collected at this time outside of what Apple uses to enable subscriptions. ' +
        'Subscription handling, including any payment details, takes place entirely within Apple’s ' +
        'systems and never reaches us.',
    }),
  },

  {
    slug: 'CyberTap-Math-Game',
    name: 'CyberTap Math Game',
    tagline: 'Mental math against the clock.',
    summary:
      'Fast-paced mental math on a neon grid. Pick the operations and the difficulty, then work ' +
      'through addition, subtraction, multiplication, division and equations with randomized ' +
      'answers, so there is no pattern to memorize. A built-in help section covers mental math ' +
      'strategies if you want to get quicker.',
    platform: 'iOS',
    status: 'live',
    store: {
      url: 'https://apps.apple.com/us/app/cybertap-math-game/id6742124750',
      price: 'Free',
      version: '1.0',
    },
    image: 'cybertap.png',
    accent: ['#22d3ee', '#34d399'],
    policy: noCollectionPolicy({
      name: 'CyberTap Math Game',
      updated: 'February 8, 2025',
    }),
  },

  {
    slug: 'points-of-interest-ri',
    name: 'Points of Interest: RI',
    tagline: 'Worth stopping for in Rhode Island.',
    summary:
      'A guide to what is worth stopping for in Rhode Island. Browse points of interest ' +
      'around the state, see what is near you as you travel, and keep track of the places ' +
      'you have already been alongside the ones you still want to reach.',
    platform: 'iOS',
    status: 'soon',
    store: null,
    image: null,
    monogram: 'RI',
    accent: ['#0ea5e9', '#14b8a6'],
    policy: {
      updated: 'September 6, 2026',
      intro:
        'Thank you for using Points of Interest: RI. This policy describes how we collect, use, ' +
        'and protect your personal information when you use our services.',
      glance: [
        { tone: 'care', title: 'Location is used', note: 'Only to show what is near you. It is never shared with third parties.' },
        { tone: 'yes', title: 'Saved places stay on your device', note: 'The places you save are stored locally.' },
        { tone: 'yes', title: 'No tracking or ads', note: 'No advertising identifiers and no third-party analytics.' },
        { tone: 'yes', title: 'Nothing is sold or shared', note: 'Your data is not sold, rented, or handed to advertisers.' },
      ],
      sections: [
        {
          title: 'Information We Collect',
          subs: [
            {
              title: 'Location Data',
              body:
                'The app uses your device’s location to show which points of interest are near you ' +
                'and to note the ones you have reached. This data is used only to power those ' +
                'features inside the app, and it is never shared with third parties.',
            },
            {
              title: 'Saved Places',
              body:
                'Places you save, mark as visited, or add to a list are stored on your device so the ' +
                'app can show them back to you across sessions.',
            },
            {
              title: 'Device & Usage Data',
              body:
                'We may collect non-personal usage data, such as crash reports or anonymized ' +
                'analytics, to improve app performance and user experience. This data does not ' +
                'contain information that personally identifies you.',
            },
          ],
        },
        {
          title: 'How We Use Your Information',
          body:
            'Location is used to surface nearby points of interest and to record the ones you have ' +
            'visited. Saved places are used to build your own lists inside the app. Diagnostic data ' +
            'is used to find and fix problems.',
        },
        {
          title: 'Data Sharing & Disclosure',
          subs: [
            {
              title: 'Third Parties',
              body:
                'We do not sell or share your personal location data with third-party advertisers.',
            },
            {
              title: 'Legal Requirements',
              body:
                'We may disclose your data if required by law, or in the good-faith belief that such ' +
                'action is necessary to comply with legal obligations or protect our rights.',
            },
          ],
        },
        {
          title: 'Data Retention & Security',
          body:
            'Your saved places remain on your device for as long as the app is installed. Deleting ' +
            'the app removes them. We take reasonable measures to protect your data, but no method ' +
            'of transmission or storage is 100% secure.',
        },
        {
          title: 'Your Choices',
          subs: [
            {
              title: 'Location Permissions',
              body:
                'You can revoke location access at any time in your device’s Settings. Without it, ' +
                'the app cannot show what is near you, though you can still browse.',
            },
            {
              title: 'Deletion',
              body: 'Removing the app deletes the places saved on your device.',
            },
          ],
        },
        POLICY_CHANGES,
      ],
    },
  },

  {
    slug: 'SplitStep-Club',
    name: 'Split Step: Club Edition',
    tagline: 'Club leagues, bookings and hitting partners.',
    summary:
      'Club tennis in one place. Join your club, play its leagues, book courts, post for a ' +
      'hitting partner and RSVP to events, with match scores and win/loss records kept per club.',
    platform: 'iOS',
    status: 'soon',
    store: null,
    image: null,
    accent: ['#22d3ee', '#6366f1'],
    policy: {
      updated: 'February 8, 2025',
      intro:
        'Thank you for using Split Step: Club Edition. This policy describes what the app collects, ' +
        'what it deliberately does not collect, and how long each kind of record is kept.',
      glance: [
        { tone: 'yes', title: 'No location, contacts or photos', note: 'The app never asks for them.' },
        { tone: 'yes', title: 'No ad IDs, no third-party analytics', note: 'And your data is never sold.' },
        { tone: 'info', title: 'Apple handles payment', note: 'Card numbers and billing addresses never reach our servers.' },
        { tone: 'care', title: 'Messages and reports are kept', note: 'They survive account deletion as a moderation trail.' },
      ],
      sections: [
        {
          title: 'Information We Collect',
          subs: [
            {
              title: 'Account data',
              list: [
                'Email address and password, handled by Firebase Authentication. Your password is never stored by us, only Firebase’s hashed credential.',
                'Display name.',
                'User ID (Firebase UID).',
              ],
            },
            {
              title: 'Club membership data',
              list: [
                'Clubs joined, your role in each club (member, pro, or admin), and join dates.',
                'League participation, match scores, and win/loss records.',
                'Ghost-record claims, which link pre-app roster history to your account.',
              ],
            },
            {
              title: 'Activity content',
              list: [
                'Direct messages.',
                'Booking requests, times, and notes.',
                'Hitting-partner posts and bids.',
                'Event RSVPs.',
                'Member reports you file, or that are filed about you, covering the reporter, the target, and the reason. These are visible to club managers.',
                'Users you have blocked.',
              ],
            },
            {
              title: 'Device and technical data',
              list: [
                'Push notification tokens (FCM), used to deliver notifications to your device.',
                'Device attestation via Apple App Attest, which verifies a genuine copy of the app. It carries no identity data.',
              ],
            },
            {
              title: 'Purchase data',
              list: [
                'Subscription status, product, expiration, and Apple transaction identifiers.',
                'A random UUID (storeToken) that binds purchases to your account.',
                'Payment details such as card numbers and billing address are handled entirely by Apple and never reach our servers.',
              ],
            },
            {
              title: 'Email',
              body: 'Booking notifications are sent by SMTP to the email address on file for your account.',
            },
          ],
        },
        {
          title: 'What We Do Not Collect',
          lead: 'Stated affirmatively, because it is as much a part of the policy as what we do collect:',
          subs: [
            {
              title: 'Never requested, never stored',
              list: [
                'No location data.',
                'No contacts.',
                'No photos.',
                'No advertising identifiers.',
                'No third-party analytics.',
              ],
            },
          ],
          callout: {
            tone: 'good',
            title: 'We do not sell your data',
            body: 'None of the information above is sold, rented, or provided to advertisers.',
          },
        },
        {
          title: 'Service Providers',
          body:
            'The app relies on a small number of providers to function: Firebase Authentication for sign-in, ' +
            'Firebase Cloud Messaging for push notifications, Apple App Attest for device verification, Apple ' +
            'for all subscription payments, and an SMTP provider to deliver booking emails. Data reaches these ' +
            'providers only insofar as it is needed to deliver those features.',
        },
        {
          title: 'Retention & Account Deletion',
          body:
            'Deleting your account removes your profile and membership records. Two categories are deliberately ' +
            'excluded from that deletion so that clubs retain a usable moderation history.',
          callout: {
            tone: 'warn',
            title: 'Direct messages and member reports are retained after account deletion',
            body:
              'Direct messages are kept as a moderation trail. Member reports, covering the reporter, the ' +
              'target and the reason, are visible to club managers and are never deleted. This is a deliberate ' +
              'exception to account deletion, and we state it here explicitly rather than leaving it implied.',
          },
        },
        POLICY_CHANGES,
      ],
    },
  },

  {
    slug: 'book-value',
    name: 'Book Value | Barcode Scanner',
    tagline: 'Scan a barcode, see what it is worth.',
    platform: 'iOS',
    status: 'soon',
    store: null,
    image: 'Book Value.png',
    accent: ['#0ea5e9', '#8b5cf6'],
    /* Not surfaced in the grid, but the route is kept so existing links resolve. */
    listed: false,
    policy: countyPolicy('Book Value | Barcode Scanner'),
  },

  {
    slug: 'travel-mississippi',
    name: 'Travel Mississippi',
    tagline: 'In development.',
    platform: 'iOS',
    status: 'soon',
    store: null,
    image: null,
    accent: ['#f59e0b', '#d946ef'],
  },

  {
    slug: 'coming-soon',
    name: 'Coming Soon',
    tagline: 'Unplanned release.',
    platform: 'iOS',
    status: 'soon',
    store: null,
    image: null,
    accent: ['#64748b', '#334155'],
  },
];

/** Cards shown on the landing page. */
export const LISTED_APPS = APPS.filter(app => app.listed !== false);

export const getApp = slug =>
  APPS.find(app => app.slug.toLowerCase() === String(slug).toLowerCase());
